<script setup lang="ts">
// Experimental read-only session preview rendered as a colour mosaic — NOT a
// live terminal. We attach to the same /ws/remote|/ws/nx fan-out as the full
// terminal but never send input and never resize the real session. xterm is
// used purely as a headless ANSI parser (hidden, fixed 80×24): we read its
// buffer and paint our own <canvas>, one small square per cell:
//   - colour  = the cell's ANSI colour (bg if set, else fg)
//   - opacity = the glyph's "ink density" (a big `Ф` is brighter than a `.`)
// Throttled repaint (~400ms) means no flicker, no selection, no scroll. If
// this doesn't read well at small sizes we'll swap the approach.
import { computed, onBeforeUnmount, onMounted, ref, shallowRef } from "vue";
import { Terminal } from "xterm";
import { terminalOutput, terminalSocketUrl } from "@/terminalStream";

const props = defineProps<{
  sessionId: string;
  targetType: "agent" | "nx";
  mode: "live" | "last";
  // The session's real terminal size — the headless parser must match it or
  // the stream (sized for the live screen) only shows its bottom rows. The
  // parent re-keys this component on size change, so treating these as fixed
  // for the component's lifetime is fine.
  cols: number;
  rows: number;
}>();

// Guard against a missing/zero size from the record.
const COLS = Math.max(1, props.cols || 80);
const ROWS = Math.max(1, props.rows || 24);
// Terminal cells are ~twice as tall as wide; mirror that so the mosaic keeps
// a terminal-like aspect (not a squashed letterbox).
const CELL_W = 3;
const CELL_H = 6;

// Aspect ratio follows the real grid so the tile isn't stretched.
const aspect = computed(() => `${COLS * CELL_W} / ${ROWS * CELL_H}`);

const canvas = ref<HTMLCanvasElement | null>(null);
const sink = ref<HTMLDivElement | null>(null);
const term = shallowRef<Terminal | null>(null);
let socket: WebSocket | null = null;
let output: ReturnType<typeof terminalOutput> | null = null;
let raf = 0;
let timer: number | null = null;
let dirty = false;

// --- glyph ink density, measured once per char on an offscreen canvas ------
const densityCache = new Map<string, number>();
let measureCtx: CanvasRenderingContext2D | null = null;
function glyphDensity(ch: string): number {
  if (!ch || ch === " ") return 0;
  const cached = densityCache.get(ch);
  if (cached !== undefined) return cached;
  if (!measureCtx) {
    const c = document.createElement("canvas");
    c.width = 8;
    c.height = 12;
    measureCtx = c.getContext("2d", { willReadFrequently: true });
    if (measureCtx) {
      measureCtx.font = "11px monospace";
      measureCtx.textBaseline = "top";
    }
  }
  if (!measureCtx) return 0.3;
  measureCtx.clearRect(0, 0, 8, 12);
  measureCtx.fillStyle = "#fff";
  measureCtx.fillText(ch, 0, 0);
  const data = measureCtx.getImageData(0, 0, 8, 12).data;
  let on = 0;
  for (let i = 3; i < data.length; i += 4) if (data[i] > 40) on++;
  const d = on / 96;
  densityCache.set(ch, d);
  return d;
}

// --- ANSI 256 → rgb --------------------------------------------------------
const BASE16 = [
  [0, 0, 0],
  [205, 49, 49],
  [13, 188, 121],
  [229, 229, 16],
  [36, 114, 200],
  [188, 63, 188],
  [17, 168, 205],
  [229, 229, 229],
  [102, 102, 102],
  [241, 76, 76],
  [35, 209, 139],
  [245, 245, 67],
  [59, 142, 234],
  [214, 112, 214],
  [41, 184, 219],
  [255, 255, 255],
];
function ansi256(i: number): [number, number, number] {
  if (i < 16) return BASE16[i] as [number, number, number];
  if (i < 232) {
    const n = i - 16;
    const r = Math.floor(n / 36);
    const g = Math.floor((n % 36) / 6);
    const b = n % 6;
    const c = (v: number) => (v === 0 ? 0 : 55 + v * 40);
    return [c(r), c(g), c(b)];
  }
  const v = 8 + (i - 232) * 10;
  return [v, v, v];
}

let defaultFg: [number, number, number] = [230, 237, 243];
function readDefaultFg() {
  const css = getComputedStyle(document.documentElement).getPropertyValue("--color-fg").trim();
  const m = /^#([0-9a-f]{6})$/i.exec(css);
  if (m) {
    defaultFg = [
      parseInt(m[1].slice(0, 2), 16),
      parseInt(m[1].slice(2, 4), 16),
      parseInt(m[1].slice(4, 6), 16),
    ];
  }
}

function paint() {
  raf = 0;
  const cv = canvas.value;
  const t = term.value;
  if (!cv || !t) return;
  const ctx = cv.getContext("2d");
  if (!ctx) return;
  const buf = t.buffer.active;
  const base = Math.max(0, buf.length - ROWS);
  // Clear to the dashboard bg so empty cells read as background.
  ctx.clearRect(0, 0, cv.width, cv.height);
  for (let y = 0; y < ROWS; y++) {
    const line = buf.getLine(base + y);
    if (!line) continue;
    for (let x = 0; x < COLS; x++) {
      const cell = line.getCell(x);
      if (!cell) continue;
      const chars = cell.getChars();
      const hasBg = !cell.isBgDefault();
      let rgb: [number, number, number];
      let alpha: number;
      if (hasBg) {
        // A coloured background block: paint it solid — these carry the
        // visual structure (selections, status bars, panes).
        rgb = cell.isBgRGB()
          ? [
              (cell.getBgColor() >> 16) & 255,
              (cell.getBgColor() >> 8) & 255,
              cell.getBgColor() & 255,
            ]
          : ansi256(cell.getBgColor());
        alpha = 0.9;
      } else {
        const density = glyphDensity(chars);
        if (density <= 0) continue;
        if (cell.isFgDefault()) rgb = defaultFg;
        else if (cell.isFgRGB())
          rgb = [
            (cell.getFgColor() >> 16) & 255,
            (cell.getFgColor() >> 8) & 255,
            cell.getFgColor() & 255,
          ];
        else rgb = ansi256(cell.getFgColor());
        alpha = Math.min(1, density * 1.4 + 0.05);
      }
      ctx.fillStyle = `rgba(${rgb[0]},${rgb[1]},${rgb[2]},${alpha})`;
      ctx.fillRect(x * CELL_W, y * CELL_H, CELL_W, CELL_H);
    }
  }
}

function schedulePaint() {
  if (!raf) raf = requestAnimationFrame(paint);
}

onMounted(() => {
  readDefaultFg();
  const cv = canvas.value;
  if (cv) {
    cv.width = COLS * CELL_W;
    cv.height = ROWS * CELL_H;
  }
  const t = new Terminal({
    cols: COLS,
    rows: ROWS,
    scrollback: 0,
    disableStdin: true,
    // Headless: opened into an off-layout sink, never shown. We read its
    // buffer, not its render.
  });
  if (sink.value) t.open(sink.value);
  term.value = t;

  const wsPath = props.targetType === "nx" ? "/ws/nx" : "/ws/remote";
  const ws = new WebSocket(terminalSocketUrl(wsPath, props.sessionId));
  const decoder = terminalOutput(
    (bytes) =>
      new Promise<void>((resolve) => {
        t.write(bytes, () => {
          dirty = true;
          resolve();
        });
      }),
    () => ws.close(),
    (frame) => {
      if (ws.readyState === WebSocket.OPEN) ws.send(frame);
    },
  );
  output = decoder;
  ws.binaryType = "arraybuffer";
  socket = ws;
  ws.onmessage = (ev) => {
    decoder.push(ev.data);
  };

  if (props.mode === "last") {
    // Drain the replay snapshot, paint once, then disconnect.
    window.setTimeout(async () => {
      ws.close();
      await decoder.drain();
      paint();
      socket = null;
    }, 700);
  } else {
    // Live: repaint on a slow cadence only when new bytes arrived.
    timer = window.setInterval(() => {
      if (dirty) {
        dirty = false;
        schedulePaint();
      }
    }, 400);
  }
});

onBeforeUnmount(() => {
  output?.dispose();
  if (timer) clearInterval(timer);
  if (raf) cancelAnimationFrame(raf);
  if (socket) {
    socket.close();
    socket = null;
  }
  if (term.value) {
    term.value.dispose();
    term.value = null;
  }
});
</script>

<template>
  <div class="mosaic" :style="{ aspectRatio: aspect }">
    <canvas ref="canvas" class="mosaic__canvas"></canvas>
    <!-- Off-layout headless xterm: parser only, never visible. -->
    <div ref="sink" class="mosaic__sink" aria-hidden="true"></div>
  </div>
</template>

<style scoped>
.mosaic {
  position: relative;
  width: 100%;
  border-radius: 4px;
  overflow: hidden;
  border: 1px solid var(--color-border);
  background: var(--color-bg);
}
.mosaic__canvas {
  display: block;
  width: 100%;
  height: 100%;
  image-rendering: pixelated;
}
.mosaic__sink {
  position: absolute;
  left: -99999px;
  top: 0;
  width: 1px;
  height: 1px;
  overflow: hidden;
  pointer-events: none;
}
</style>
